import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import type { PartnerProgramMembership } from "../../shared/types";
import { PORTAL_NAV } from "../lib/nav";
import { useTheme } from "../context/ThemeContext";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { PortalModeToggle } from "./PortalModeToggle";

function initials(first: string, last: string): string {
  const a = first.trim()[0] ?? "";
  const b = last.trim()[0] ?? "";
  return (a + b || first.trim().slice(0, 2) || "P").toUpperCase();
}

function letter(name: string): string {
  return (name.trim()[0] ?? "P").toUpperCase();
}

/** The app top bar: program switcher, primary nav, mode toggle, and account menu. */
export function PortalTopBar() {
  const { mode, setMode } = useTheme();
  const { session, currentMembership, selectProgram, logout } = usePartnerAuth();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!switcherRef.current?.contains(e.target as Node)) setSwitcherOpen(false);
      if (!acctRef.current?.contains(e.target as Node)) setAcctOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const partner = session?.partner ?? null;
  const programs = session?.programs ?? [];

  function switchProgram(p: PartnerProgramMembership) {
    setSwitcherOpen(false);
    if (p.agreementSigned && !p.needsResign) {
      selectProgram(p.programId);
      navigate("/home");
    } else {
      navigate(`/onboarding/${p.programId}`);
    }
  }

  async function onLogout() {
    setAcctOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="topbar">
      <div className="prodsw-wrap" ref={switcherRef}>
        <button
          type="button"
          className="prodsw"
          aria-haspopup="true"
          aria-expanded={switcherOpen}
          onClick={(e) => {
            e.stopPropagation();
            setSwitcherOpen((o) => !o);
          }}
        >
          <span className="plogo" aria-hidden="true">
            {currentMembership ? letter(currentMembership.productName) : "P"}
          </span>
          <span className="psw-name">
            <span>{currentMembership?.productName ?? "Partner Portal"}</span>
            <span className="psw-sub">Partners</span>
          </span>
          <svg className="psw-chev" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 10l5 5 5-5" />
          </svg>
        </button>
        {switcherOpen && (
          <div className="prod-menu" onClick={(e) => e.stopPropagation()}>
            {programs.map((p) => (
              <button key={p.programId} type="button" onClick={() => switchProgram(p)}>
                <span className="pm-logo" aria-hidden="true">
                  {letter(p.productName)}
                </span>
                {p.productName}
                {!(p.agreementSigned && !p.needsResign) && (
                  <span className="need-sign">{p.needsResign ? "Re-sign" : "Sign"}</span>
                )}
              </button>
            ))}
            <button className="prod-all" type="button" onClick={() => navigate("/programs")}>
              All programs
            </button>
          </div>
        )}
      </div>

      <nav className="topnav">
        {PORTAL_NAV.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="topbar-right">
        <PortalModeToggle />
        {partner && (
          <div className="acctwrap" ref={acctRef}>
            <button
              type="button"
              className="av"
              aria-haspopup="true"
              aria-expanded={acctOpen}
              onClick={(e) => {
                e.stopPropagation();
                setAcctOpen((o) => !o);
              }}
            >
              {partner.avatarUrl ? (
                <img className="av-img" src={partner.avatarUrl} alt="" />
              ) : (
                initials(partner.firstName, partner.lastName)
              )}
            </button>
            {acctOpen && (
              <div className="acct-menu" onClick={(e) => e.stopPropagation()}>
                <div className="am-head">
                  <span className="am-av">
                    {partner.avatarUrl ? (
                      <img className="av-img" src={partner.avatarUrl} alt="" />
                    ) : (
                      initials(partner.firstName, partner.lastName)
                    )}
                  </span>
                  <div className="am-who">
                    <div className="am-name">
                      {partner.firstName} {partner.lastName}
                    </div>
                    <div className="am-mail">{partner.email}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAcctOpen(false);
                    navigate("/profile");
                  }}
                >
                  Profile
                </button>
                <div className="am-theme">
                  <span>Theme</span>
                  <div className="mode-switch">
                    <button
                      type="button"
                      className={mode === "dark" ? "on" : ""}
                      onClick={() => setMode("dark")}
                    >
                      Dark
                    </button>
                    <button
                      type="button"
                      className={mode === "light" ? "on" : ""}
                      onClick={() => setMode("light")}
                    >
                      Light
                    </button>
                  </div>
                </div>
                <button className="am-out" type="button" onClick={onLogout}>
                  Log out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
